import { useApiClient } from "@/src/hooks/useApiClient";
import { useQueryErrorToast } from "@/src/hooks/useQueryErrorToast";
import { useTranslations } from "@/src/i18n/i18n";

/** Cinquante cœurs par page — la borne du serveur par défaut. */
export const LIKES_PAGE_SIZE = 50;

/**
 * **Qui** a aimé une publication.
 *
 * L'API publique n'en rend que le nombre, et s'y tient : à chaque élément du
 * fil, diffuser des milliers d'identités serait absurde. La question est ici
 * tout autre — un compte qui aime systématiquement les publications d'un même
 * autre compte est un motif qu'aucun compteur ne montre, et c'est exactement ce
 * qu'un modérateur vient chercher sur un dossier de harcèlement.
 *
 * ⚠️ **Chargé seulement quand la section est ouverte** (`isEnabled`). Non pas
 * pour le registre — un cœur est public, rien n'est journalisé — mais parce
 * qu'une publication populaire en porte des milliers : les rapatrier pour une
 * fiche qu'on ouvre afin de lire un commentaire serait payer une page entière
 * pour rien.
 */
export function usePostLikes(postId: number, page: number, isEnabled: boolean) {
  const $api = useApiClient();
  const t = useTranslations("posts");

  const { data, error, isError, isLoading } = $api.useQuery(
    "get",
    "/api/v1/moderation/post/{id}/like",
    {
      params: {
        path: { id: postId },
        query: {
          limit: LIKES_PAGE_SIZE,
          offset: (page - 1) * LIKES_PAGE_SIZE,
        },
      },
    },
    { enabled: isEnabled && postId > 0 },
  );

  useQueryErrorToast(isError, t("likes-error"), error);

  const total = data?.data?.total ?? 0;

  return {
    likes: data?.data?.likes ?? [],
    total,
    pageCount: Math.max(1, Math.ceil(total / LIKES_PAGE_SIZE)),
    isLoading,
    isError,
  };
}
